/**
 * Custom `@calculatePosition` for ember-basic-dropdown that anchors the
 * dropdown to the viewport using `position: fixed` rather than the default
 * absolute positioning in body/document coordinates.
 *
 * The default `calculateWormholedPosition` uses
 *   style.top = triggerRect.top + window.pageYOffset + triggerHeight
 * which only stays stable while the trigger moves with the page. When the
 * trigger lives inside a `position: sticky` container, the trigger stops
 * moving in the viewport once the sticky element hits its top stop, but
 * `pageYOffset` keeps growing, so the dropdown drifts away from the trigger.
 *
 * Returning viewport-relative coords with `position: fixed` keeps the dropdown
 * pinned to the trigger's current viewport position regardless of page scroll
 * or sticky state. ember-basic-dropdown re-runs this on every scroll/resize,
 * so the dropdown still follows when the trigger does move in the viewport.
 *
 * Handles horizontal `auto`/`auto-left`/`auto-right`/`right`/`center`/`left`
 * and vertical `above`/`below`/auto the same way as the upstream calc, just
 * using viewport coords throughout.
 */
export default function fixedDropdownPosition(
  trigger,
  content,
  _destination,
  {
    horizontalPosition,
    verticalPosition,
    matchTriggerWidth,
    previousHorizontalPosition,
    previousVerticalPosition,
  }
) {
  let {
    left: triggerLeft,
    top: triggerTop,
    width: triggerWidth,
    height: triggerHeight,
  } = trigger.getBoundingClientRect();
  let { height: dropdownHeight, width: dropdownWidth } = content.getBoundingClientRect();
  let viewportWidth = document.body.clientWidth || window.innerWidth;
  let viewportHeight = window.innerHeight;

  let style = { position: 'fixed' };

  if (matchTriggerWidth) {
    dropdownWidth = triggerWidth;
    style.width = triggerWidth;
  }

  if (horizontalPosition === 'auto' || horizontalPosition === 'auto-left') {
    let leftVisible =
      Math.min(viewportWidth, triggerLeft + dropdownWidth) - Math.max(0, triggerLeft);
    let rightVisible =
      Math.min(viewportWidth, triggerLeft + triggerWidth) -
      Math.max(0, triggerLeft + triggerWidth - dropdownWidth);
    if (dropdownWidth > leftVisible && rightVisible > leftVisible) {
      horizontalPosition = 'right';
    } else if (dropdownWidth > rightVisible && leftVisible > rightVisible) {
      horizontalPosition = 'left';
    } else {
      horizontalPosition = previousHorizontalPosition || 'left';
    }
  } else if (horizontalPosition === 'auto-right') {
    let leftVisible =
      Math.min(viewportWidth, triggerLeft + dropdownWidth) - Math.max(0, triggerLeft);
    let rightVisible =
      Math.min(viewportWidth, triggerLeft + triggerWidth) -
      Math.max(0, triggerLeft + triggerWidth - dropdownWidth);
    if (dropdownWidth > rightVisible && leftVisible > rightVisible) {
      horizontalPosition = 'left';
    } else if (dropdownWidth > leftVisible && rightVisible > leftVisible) {
      horizontalPosition = 'right';
    } else {
      horizontalPosition = previousHorizontalPosition || 'right';
    }
  }

  if (horizontalPosition === 'right') {
    style.right = viewportWidth - (triggerLeft + triggerWidth);
  } else if (horizontalPosition === 'center') {
    style.left = triggerLeft + (triggerWidth - dropdownWidth) / 2;
  } else {
    style.left = triggerLeft;
  }

  if (verticalPosition === 'above') {
    style.top = triggerTop - dropdownHeight;
  } else if (verticalPosition === 'below') {
    style.top = triggerTop + triggerHeight;
  } else {
    let enoughRoomBelow = triggerTop + triggerHeight + dropdownHeight < viewportHeight;
    let enoughRoomAbove = triggerTop > dropdownHeight;
    if (!enoughRoomBelow && !enoughRoomAbove) {
      verticalPosition = 'below';
    } else if (previousVerticalPosition === 'below' && !enoughRoomBelow && enoughRoomAbove) {
      verticalPosition = 'above';
    } else if (previousVerticalPosition === 'above' && !enoughRoomAbove && enoughRoomBelow) {
      verticalPosition = 'below';
    } else if (!previousVerticalPosition) {
      verticalPosition = enoughRoomBelow ? 'below' : 'above';
    } else {
      verticalPosition = previousVerticalPosition;
    }
    style.top = triggerTop + (verticalPosition === 'below' ? triggerHeight : -dropdownHeight);
  }

  return { horizontalPosition, verticalPosition, style };
}
